<template>
  <div class="user-groups-modal modal-card bg-white">
    <header class="user-groups-modal__header">
      <div>
        <h2>Groupes d’utilisateurs</h2>
        <p>Créez les groupes du domaine et choisissez leurs membres.</p>
      </div>
      <button type="button" aria-label="Fermer" @click="close">
        <i class="fas fa-times" />
      </button>
    </header>

    <div class="user-groups-modal__body">
      <aside class="user-groups-sidebar">
        <div class="user-groups-list" aria-label="Liste des groupes">
          <button
            v-for="group in groups"
            :key="group.uuid"
            type="button"
            :class="{ 'is-active': selectedGroupUuid === group.uuid }"
            @click="selectedGroupUuid = group.uuid"
          >
            <span>{{ group.label }}</span>
            <small>{{ memberCount(group.uuid) }}</small>
          </button>
          <p v-if="!loading && !groups.length">Aucun groupe pour ce domaine.</p>
        </div>

        <form class="user-groups-create" @submit.prevent="createGroup">
          <label for="new-user-group">Créer un nouveau groupe</label>
          <div>
            <input
              id="new-user-group"
              v-model.trim="newGroupLabel"
              type="text"
              placeholder="Nom du groupe"
            >
            <button type="submit" :disabled="!newGroupLabel || isCreating" aria-label="Ajouter le groupe">
              <i :class="isCreating ? 'fas fa-spinner fa-spin' : 'fas fa-plus'" />
            </button>
          </div>
        </form>
      </aside>

      <main class="user-groups-members">
        <div v-if="loading" class="user-groups-state">
          <i class="fas fa-spinner fa-spin" />
          <span>Chargement des groupes…</span>
        </div>

        <div v-else-if="loadError" class="user-groups-state user-groups-state--error">
          <i class="fas fa-exclamation-circle" />
          <span>{{ loadError }}</span>
          <button type="button" @click="loadData">Réessayer</button>
        </div>

        <template v-else-if="selectedGroup">
          <div class="user-groups-members__toolbar">
            <form @submit.prevent="renameSelectedGroup">
              <input
                v-model.trim="selectedGroupLabel"
                type="text"
                aria-label="Nom du groupe"
              >
              <button
                type="submit"
                :disabled="!canRenameSelectedGroup || isRenaming"
                title="Enregistrer le nom du groupe"
              >
                <i :class="isRenaming ? 'fas fa-spinner fa-spin' : 'fas fa-check'" />
              </button>
            </form>

            <button
              type="button"
              class="user-groups-delete"
              title="Supprimer ce groupe"
              @click="confirmDeleteSelectedGroup"
            >
              <i class="fas fa-trash-alt" />
            </button>
          </div>

          <p class="user-groups-members__notice">
            <i class="fas fa-info-circle" aria-hidden="true" />
            <span>Les administrateurs sont automatiquement ajoutés à chaque groupe créé.</span>
          </p>

          <div class="user-groups-search">
            <i class="fas fa-search" />
            <input
              v-model.trim="userSearch"
              type="search"
              placeholder="Rechercher un utilisateur"
              aria-label="Rechercher un utilisateur dans le groupe"
            >
          </div>

          <div class="user-groups-user-list">
            <div v-if="usersLoading" class="user-groups-user-list__state">
              <i class="fas fa-spinner fa-spin" />
              <span>Chargement des utilisateurs…</span>
            </div>

            <div v-else-if="usersError" class="user-groups-user-list__state user-groups-user-list__state--error">
              <span>{{ usersError }}</span>
              <button type="button" @click="loadUsers">Réessayer</button>
            </div>

            <template v-else>
              <label
                v-for="user in users"
                :key="user.uuid"
                :class="{ 'is-locked': isDirector(user) }"
                :aria-disabled="isDirector(user) ? 'true' : null"
                :title="isDirector(user) ? 'Administrateur ajouté automatiquement' : null"
              >
                <input
                  type="checkbox"
                  :checked="userIsInSelectedGroup(user)"
                  :disabled="isDirector(user) || Boolean(updatingUsers[user.uuid])"
                  @change="toggleUser(user, $event.target.checked)"
                >
                <span class="user-groups-user-list__identity">
                  <strong>{{ displayName(user) }}</strong>
                  <small>{{ user.email || user.internal_id_number || 'Sans identifiant' }}</small>
                </span>
                <i v-if="updatingUsers[user.uuid]" class="fas fa-spinner fa-spin" />
              </label>

              <p v-if="!users.length" class="user-groups-no-user">
                Aucun utilisateur ne correspond à cette recherche.
              </p>
            </template>
          </div>

          <footer v-if="!usersError" class="user-groups-users-pagination">
            <span>{{ usersPaginationLabel }}</span>
            <div v-if="usersPageCount > 1">
              <button
                type="button"
                :disabled="usersCurrentPage === 1 || usersLoading"
                aria-label="Page précédente"
                @click="goToUsersPage(usersCurrentPage - 1)"
              >
                <i class="fas fa-chevron-left" />
              </button>
              <span>Page {{ usersCurrentPage }} / {{ usersPageCount }}</span>
              <button
                type="button"
                :disabled="usersCurrentPage === usersPageCount || usersLoading"
                aria-label="Page suivante"
                @click="goToUsersPage(usersCurrentPage + 1)"
              >
                <i class="fas fa-chevron-right" />
              </button>
            </div>
          </footer>
        </template>

        <div v-else class="user-groups-state">
          <i class="fas fa-users" />
          <span>Créez ou sélectionnez un groupe pour gérer ses membres.</span>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { http } from '@/services/api.service'

export default {
  name: 'UserGroupsModal',
  data () {
    return {
      loading: true,
      loadError: '',
      groups: [],
      users: [],
      selectedGroupUuid: null,
      selectedGroupLabel: '',
      newGroupLabel: '',
      userSearch: '',
      isCreating: false,
      isRenaming: false,
      updatingUsers: {},
      usersCurrentPage: 1,
      usersPageSize: 25,
      usersTotalCount: 0,
      usersLoading: false,
      usersError: '',
      usersRequestId: 0,
      userSearchTimer: null
    }
  },
  computed: {
    selectedGroup () {
      return this.groups.find(group => group.uuid === this.selectedGroupUuid) || null
    },
    canRenameSelectedGroup () {
      return Boolean(
        this.selectedGroup &&
        this.selectedGroupLabel &&
        this.selectedGroupLabel !== this.selectedGroup.label
      )
    },
    usersPageCount () {
      return Math.max(1, Math.ceil(this.usersTotalCount / this.usersPageSize))
    },
    usersPaginationLabel () {
      if (!this.usersTotalCount) return '0 utilisateur'
      const first = ((this.usersCurrentPage - 1) * this.usersPageSize) + 1
      const last = Math.min(this.usersCurrentPage * this.usersPageSize, this.usersTotalCount)
      return `${first}–${last} sur ${this.usersTotalCount.toLocaleString('fr-FR')}`
    }
  },
  watch: {
    selectedGroup: {
      immediate: true,
      handler (group, previousGroup) {
        this.selectedGroupLabel = group ? group.label : ''
        const resetsSearch = this.userSearch !== ''
        this.userSearch = ''
        this.usersCurrentPage = 1

        if (
          group &&
          group.uuid !== previousGroup?.uuid &&
          !this.loading &&
          !resetsSearch
        ) {
          this.loadUsers()
        }
      }
    },
    userSearch () {
      this.scheduleUserSearch()
    }
  },
  created () {
    this.loadData()
  },
  beforeDestroy () {
    window.clearTimeout(this.userSearchTimer)
  },
  methods: {
    async loadData () {
      this.loading = true
      this.loadError = ''
      try {
        const groupsResponse = await http.get('/groups')
        this.groups = Array.isArray(groupsResponse.data) ? groupsResponse.data : []

        if (!this.groups.some(group => group.uuid === this.selectedGroupUuid)) {
          this.selectedGroupUuid = this.groups[0]?.uuid || null
        }

        await this.loadUsers()
      } catch (error) {
        this.loadError = 'Impossible de charger les groupes d’utilisateurs.'
        this.notifyError('Impossible de charger les groupes d’utilisateurs.')
      } finally {
        this.loading = false
      }
    },
    scheduleUserSearch () {
      window.clearTimeout(this.userSearchTimer)
      this.userSearchTimer = window.setTimeout(() => {
        this.usersCurrentPage = 1
        this.loadUsers()
      }, 300)
    },
    async loadUsers () {
      const requestId = ++this.usersRequestId
      this.usersLoading = true
      this.usersError = ''

      try {
        const { data } = await http.get('/user/eligibles-for-user-groups', {
          params: {
            page: this.usersCurrentPage,
            per_page: this.usersPageSize,
            search: this.userSearch.trim(),
            group: this.selectedGroup?.uuid || ''
          }
        })

        if (requestId !== this.usersRequestId) return

        this.users = Array.isArray(data?.rows) ? data.rows : []
        this.usersTotalCount = Number(data?.totalCount || 0)

        const lastPage = Math.max(1, Math.ceil(this.usersTotalCount / this.usersPageSize))
        if (this.usersCurrentPage > lastPage) {
          this.usersCurrentPage = lastPage
          return this.loadUsers()
        }
      } catch (error) {
        if (requestId !== this.usersRequestId) return
        this.users = []
        this.usersTotalCount = 0
        this.usersError = 'Impossible de charger les utilisateurs.'
      } finally {
        if (requestId === this.usersRequestId) {
          this.usersLoading = false
        }
      }
    },
    goToUsersPage (page) {
      if (page < 1 || page > this.usersPageCount || page === this.usersCurrentPage) return
      this.usersCurrentPage = page
      this.loadUsers()
    },
    async createGroup () {
      if (!this.newGroupLabel || this.isCreating) return
      this.isCreating = true

      try {
        const { data } = await http.post('/groups', { label: this.newGroupLabel })
        this.newGroupLabel = ''
        await this.loadData()
        this.selectedGroupUuid = data?.uuid || this.groups[0]?.uuid || null
        this.notifySuccess('Groupe créé.')
        this.$emit('groups-updated')
      } catch (error) {
        this.notifyError('Erreur lors de la création du groupe.')
      } finally {
        this.isCreating = false
      }
    },
    async renameSelectedGroup () {
      if (!this.canRenameSelectedGroup || this.isRenaming) return
      this.isRenaming = true

      try {
        const { data } = await http.post(`/groups/${this.selectedGroup.uuid}`, {
          label: this.selectedGroupLabel
        })
        const index = this.groups.findIndex(group => group.uuid === this.selectedGroup.uuid)
        if (index >= 0) {
          this.$set(this.groups, index, {
            ...data,
            users_count: this.groups[index].users_count
          })
        }
        this.notifySuccess('Groupe mis à jour.')
        this.$emit('groups-updated')
      } catch (error) {
        this.notifyError('Erreur lors de la mise à jour du groupe.')
      } finally {
        this.isRenaming = false
      }
    },
    confirmDeleteSelectedGroup () {
      if (!this.selectedGroup) return

      this.$buefy.dialog.confirm({
        title: 'Supprimer ce groupe ?',
        message: `Le groupe « ${this.selectedGroup.label} » sera supprimé définitivement.`,
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteSelectedGroup()
      })
    },
    async deleteSelectedGroup () {
      const group = this.selectedGroup
      if (!group) return

      try {
        await http.delete(`/groups/${group.uuid}`)
        this.groups = this.groups.filter(item => item.uuid !== group.uuid)
        this.users.forEach(user => {
          user.groups = (user.groups || []).filter(item => item.uuid !== group.uuid)
        })
        this.selectedGroupUuid = this.groups[0]?.uuid || null
        this.notifySuccess('Groupe supprimé.')
        this.$emit('groups-updated')
      } catch (error) {
        this.notifyError('Erreur lors de la suppression du groupe.')
      }
    },
    async toggleUser (user, shouldBelong) {
      if (!this.selectedGroup || this.isDirector(user) || this.updatingUsers[user.uuid]) return
      const group = this.selectedGroup
      this.$set(this.updatingUsers, user.uuid, true)

      try {
        if (shouldBelong) {
          await http.post(`/groups/${group.uuid}/user`, { user: user.uuid })
          const existingGroups = Array.isArray(user.groups) ? user.groups : []
          if (!existingGroups.some(item => item.uuid === group.uuid)) {
            this.$set(user, 'groups', [...existingGroups, group])
          }
        } else {
          await http.delete(`/groups/${group.uuid}/user/${user.uuid}`)
          this.$set(user, 'groups', (user.groups || []).filter(item => item.uuid !== group.uuid))
        }
        this.adjustGroupMemberCount(group.uuid, shouldBelong ? 1 : -1)
        this.$emit('groups-updated')
        await this.loadUsers()
      } catch (error) {
        this.notifyError('Impossible de modifier les membres du groupe.')
      } finally {
        this.$delete(this.updatingUsers, user.uuid)
      }
    },
    userIsInSelectedGroup (user) {
      return Boolean(this.selectedGroup && (user.groups || []).some(group => group.uuid === this.selectedGroup.uuid))
    },
    isDirector (user) {
      return user?.management_role === 'director'
    },
    memberCount (groupUuid) {
      const group = this.groups.find(item => item.uuid === groupUuid)
      return Number(group?.users_count || 0)
    },
    adjustGroupMemberCount (groupUuid, difference) {
      const group = this.groups.find(item => item.uuid === groupUuid)
      if (!group) return
      this.$set(group, 'users_count', Math.max(0, Number(group.users_count || 0) + difference))
    },
    displayName (user) {
      const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ')
      return fullName || user.email || user.internal_id_number || 'Utilisateur'
    },
    close () {
      this.$emit('groups-updated')
      this.$parent.close()
    },
    notifySuccess (message) {
      this.$notify({ group: 'maviepro-success', text: message })
    },
    notifyError (message) {
      this.$notify({ group: 'maviepro-error', text: message })
    }
  }
}
</script>

<style scoped>
.user-groups-modal {
  width: min(72rem, calc(100vw - 2rem));
  height: min(46rem, calc(100vh - 3rem));
  overflow: hidden;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.user-groups-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.user-groups-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.user-groups-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.user-groups-modal__header > button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.user-groups-modal__header > button:hover {
  background: #f8fafc;
  color: #0f172a;
}

.user-groups-modal__body {
  display: grid;
  height: calc(100% - 5.3rem);
  grid-template-columns: minmax(15rem, 20rem) minmax(0, 1fr);
}

.user-groups-sidebar {
  display: flex;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #e5e7eb;
  background: #f8fafc;
  padding: 1rem;
  padding-bottom: 1.5rem;
}

.user-groups-create {
  flex: 0 0 auto;
  margin-top: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.user-groups-create label {
  display: block;
  margin-bottom: 0.4rem;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.user-groups-create > div {
  display: flex;
  min-height: 2.75rem;
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 9px;
  background: #fff;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.user-groups-create > div:focus-within,
.user-groups-search:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.user-groups-create input {
  min-width: 0;
  flex: 1;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0.6875rem 0.875rem;
  color: #374151;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.25rem;
  outline: none;
}

.user-groups-create button {
  flex: 0 0 2.75rem;
  background: #2563eb;
  color: #fff;
}

.user-groups-create button:disabled {
  background: #bfdbfe;
  cursor: not-allowed;
}

.user-groups-list {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.35rem;
  overflow-y: auto;
}

.user-groups-list > button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.7rem 0.75rem;
  border-radius: 0.625rem;
  color: #475569;
  text-align: left;
}

.user-groups-list > button:hover {
  background: #e2e8f0;
}

.user-groups-list > button.is-active {
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 700;
}

.user-groups-list small {
  display: inline-flex;
  min-width: 1.6rem;
  height: 1.6rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
}

.user-groups-list > p {
  padding: 2rem 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
}

.user-groups-members {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  padding: 1.25rem;
  padding-bottom: 1.5rem;
}

.user-groups-members__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.user-groups-members__toolbar form {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 0.5rem;
}

.user-groups-members__toolbar input {
  min-width: 0;
  min-height: 2.75rem;
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 9px;
  background: #fff;
  box-shadow: none;
  padding: 0.6875rem 0.875rem;
  color: #374151;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.user-groups-members__toolbar input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.user-groups-members__toolbar form button,
.user-groups-delete {
  display: inline-flex;
  width: 2.5rem;
  height: 2.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.625rem;
}

.user-groups-members__toolbar form button {
  background: #dcfce7;
  color: #15803d;
}

.user-groups-members__toolbar form button:disabled {
  background: #f1f5f9;
  color: #94a3b8;
}

.user-groups-delete {
  background: #fef2f2;
  color: #dc2626;
}

.user-groups-members__notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.8rem;
  line-height: 1.4;
}

.user-groups-members__notice i {
  margin-top: 0.15rem;
}

.user-groups-search {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  border: 1px solid #d1d5db;
  border-radius: 9px;
  background: #fff;
  padding: 0 0.875rem;
  color: #9ca3af;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.user-groups-search input {
  min-width: 0;
  width: 100%;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0.6875rem 0;
  color: #374151;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.25rem;
  outline: none;
}

.user-groups-create input::placeholder,
.user-groups-members__toolbar input::placeholder,
.user-groups-search input::placeholder {
  color: #9ca3af;
  opacity: 1;
}

.user-groups-user-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 1rem;
  overflow-y: auto;
}

.user-groups-user-list > label {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.7rem 0.8rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  cursor: pointer;
}

.user-groups-user-list > label:hover {
  background: #f8fafc;
}

.user-groups-user-list > label.is-locked,
.user-groups-user-list > label.is-locked:hover {
  border-color: #e2e8f0;
  background: #f1f5f9;
  cursor: not-allowed;
}

.user-groups-user-list > label.is-locked .user-groups-user-list__identity strong,
.user-groups-user-list > label.is-locked .user-groups-user-list__identity small {
  color: #94a3b8;
}

.user-groups-user-list > label.is-locked input {
  cursor: not-allowed;
}

.user-groups-user-list__state {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: #64748b;
  font-size: 0.875rem;
}

.user-groups-user-list__state--error {
  flex-direction: column;
  color: #b91c1c;
}

.user-groups-user-list__state--error button {
  padding: 0.5rem 0.75rem;
  border: 1px solid #93c5fd;
  border-radius: 7px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
}

.user-groups-user-list input {
  width: 1rem;
  height: 1rem;
  accent-color: #2563eb;
}

.user-groups-user-list__identity {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.user-groups-user-list__identity strong,
.user-groups-user-list__identity small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-groups-user-list__identity strong {
  color: #334155;
  font-size: 0.875rem;
}

.user-groups-user-list__identity small {
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.75rem;
}

.user-groups-users-pagination {
  display: flex;
  min-height: 2.75rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #edf0f3;
  color: #6b7280;
  font-size: 0.75rem;
}

.user-groups-users-pagination > div {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.user-groups-users-pagination button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #fff;
  color: #4b5563;
}

.user-groups-users-pagination button:hover:not(:disabled) {
  border-color: #93c5fd;
  color: #2563eb;
}

.user-groups-users-pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.user-groups-state,
.user-groups-no-user {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  color: #64748b;
  text-align: center;
}

.user-groups-state--error {
  flex-direction: column;
  color: #b91c1c;
}

.user-groups-state--error button {
  padding: 0.6rem 0.9rem;
  border: 1px solid #93c5fd;
  border-radius: 9px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
}

.user-groups-modal button:focus-visible,
.user-groups-user-list input:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

@media (max-width: 760px) {
  .user-groups-modal {
    height: calc(100vh - 1rem);
  }

  .user-groups-modal__body {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }

  .user-groups-sidebar {
    max-height: 15rem;
    border-right: 0;
    border-bottom: 1px solid #e5e7eb;
  }
}
</style>
